import axios from 'axios';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../Contexts/AuthContext';

const useApiService = (apiAddress, organizationIdOverride) => {
  // const apiAddress = CONFIG.SERVER;
  const { getAccessTokenSilently } = useAuth();

  const { organizationId: routeOrganizationId } = useParams();
  const organizationId = organizationIdOverride ?? routeOrganizationId;

  return useMemo(() => {
    const get = async (endpoint, options = {}) => {
      const token = await getAccessTokenSilently();
      const url = `${apiAddress}${endpoint}`;
      return await axios({
        method: 'get',
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
        },
        validateStatus: function (status) {
          // Treat 2xx and 401 as valid statuses.
          return (status >= 200 && status < 300) || status === 401;
        },
        ...options,
      });
    };

    const put = async (endpoint, body) => {
      const token = await getAccessTokenSilently();
      return await axios.put(`${apiAddress}${endpoint}`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
        },
      });
    };

    const post = async (endpoint, body, config = {}) => {
      const token = await getAccessTokenSilently();
      return await axios.post(`${apiAddress}${endpoint}`, body, {
        ...config,
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
        },
      });
    };

    const postStream = async (endpoint, body) => {
      const token = await getAccessTokenSilently();
      return await axios.post(`${apiAddress}${endpoint}`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
          Connection: 'keep-alive',
          'X-Accel-Buffering': 'no',
        },
        responseType: 'stream',
        adapter: 'fetch',
      });
    };

    const del = async (endpoint) => {
      const token = await getAccessTokenSilently();
      return await axios.delete(`${apiAddress}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
        },
      });
    };

    const putUpload = async (endpoint, formData) => {
      const token = await getAccessTokenSilently();
      return await axios.put(`${apiAddress}${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
          'Content-Type': 'multipart/form-data',
        },
      });
    };

    const postUpload = async (endpoint, formData) => {
      const token = await getAccessTokenSilently();
      return await axios.post(`${apiAddress}${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
          'Content-Type': 'multipart/form-data',
        },
      });
    };

    const patch = async (endpoint, body) => {
      const token = await getAccessTokenSilently();
      return await axios.patch(`${apiAddress}${endpoint}`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Key': organizationId,
        },
      });
    };

    return {
      get,
      put,
      post,
      del,
      putUpload,
      postUpload,
      patch,
      postStream,
    };
  }, [getAccessTokenSilently, apiAddress, organizationId]);
};

export default useApiService;
